import { useMutation, useQueryClient } from '@tanstack/react-query';
import { selectCommunity } from 'core/selectors/communities';
import { Nullable, Community, Image } from 'core/types';
import { patchCommunityMutationKey } from 'core/utils/mutation-key-factory';
import { useServices } from 'hooks';
import { communityKeys } from 'core/utils/query-key-factory';
import { isUndefined } from 'utils/is';
import { BaseHttpError } from '../../services/api/errors';
import { PatchCommunityInput } from '../../services/api/api-client/types';

export interface PatchCommunityMutationInput extends Partial<
	Omit<PatchCommunityInput, 'image' | 'name'>
> {
	communityId: string;
	name: string;
	image?: Nullable<Image>;
	setup_status?: number;
}

export const usePatchCommunity = () => {
	const { api, uploader } = useServices();

	const queryClient = useQueryClient();

	const mutation = useMutation<
		Community,
		BaseHttpError,
		PatchCommunityMutationInput
	>({
		mutationFn: async ({ communityId, image, ...input }) => {
			const remoteImageUuid =
				(await uploader.uploadImage(image))?.uuid ?? null;

			return api
				.patchCommunity(communityId, {
					...input,
					...(!isUndefined(image) ? { image: remoteImageUuid } : {}),
				})
				.then(response => response.data);
		},
		mutationKey: [patchCommunityMutationKey],
		onSuccess: (result, variables) => {
			const prev = queryClient.getQueryData<Community>(
				communityKeys.detail(variables.communityId)
			);

			let sectionsChanged = false;
			if (variables.availableSections !== undefined) {
				if (!prev) {
					sectionsChanged = true;
				} else {
					const prevSections = [
						...(prev.has_announcements ? ['announcements'] : []),
						...(prev.has_documents ? ['documents'] : []),
						...(prev.has_well_wishes ? ['well_wishes'] : []),
						...(prev.has_photos ? ['photos'] : []),
					].sort();
					const normalize = (a: string[]) => [...(a ?? [])].sort();
					sectionsChanged =
						normalize(prevSections).join(',') !==
						normalize(variables.availableSections).join(',');
				}
			}

			queryClient.setQueryData(
				communityKeys.detail(result.slug),
				selectCommunity(result)
			);

			if (result.slug !== variables.communityId) {
				queryClient.removeQueries({
					queryKey: communityKeys.detail(variables.communityId),
				});
			}

			if (sectionsChanged) {
				queryClient.invalidateQueries({
					queryKey: communityKeys.feed(result.slug),
				});
			}
		},
	});

	return mutation;
};
